import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useReviews } from 'features/review/hooks/useReviews';
import { useToggleReviewVisibility } from 'features/review/hooks/useToggleReviewVisibility';
import { usePrefetchReviews } from 'features/review/hooks/usePrefetchReviews';
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  User,
  MessageSquare,
  Search,
  Download
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDateTime } from 'utils/dateFormatter';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import reviewServices from 'features/review/services/review.service';

const ReviewManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL params
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialLimit = parseInt(searchParams.get('limit')) || 10;
  const initialRating = searchParams.get('rating') || '';
  const initialUserType = searchParams.get('userType') || '';

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [rating, setRating] = useState(initialRating);
  const [userType, setUserType] = useState(initialUserType);

  const initialSearch =
  searchParams.get('search') || '';

const [search, setSearch] =
  useState(initialSearch);

  const initialSortBy =
  searchParams.get('sortBy') || 'ratedAt';

const initialSortOrder =
  searchParams.get('sortOrder') || 'desc';

const [sortBy, setSortBy] =
  useState(initialSortBy);

const [sortOrder, setSortOrder] =
  useState(initialSortOrder);

  // Filters
 const filters = {
  page,
  limit,
  rating: rating || undefined,
  userType: userType || undefined,
  search: search || undefined,
  sortBy,
  sortOrder,
};

  // Fetch
  const { data: response, isLoading, isError, error } = useReviews(filters);
//   const { mutate: toggleVisibility, isPending: isToggling } =
//     useToggleReviewVisibility();

const [togglingKey, setTogglingKey] = useState(null);
const { mutate: toggleVisibility } = useToggleReviewVisibility();

  const { data: profileData } = useUserProfile();
  const user = profileData?.data;

  const restrictedRoles = [
    "delivery_manager",
    "travel_manager",
    "category_manager",
    "operations_manager",
    "finance_manager",
  ];

  const showPermissionError = restrictedRoles.includes(user?.role?.name);

  const reviews = response?.data || [];
const pagination = response?.pagination || {};

const handleExport =
  async () => {
    try {

      const response =
        await reviewServices.exportReviews(
          {
            search,
            rating,
            userType,
            sortBy,
            sortOrder,
          }
        );

      const blob =
        new Blob(
          [response.data],
          {
            type: 'text/csv',
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          'a'
        );

      link.href = url;

      link.download =
        `reviews-${Date.now()}.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );

    } catch (error) {

      console.error(
        'Review export failed',
        error
      );

    }
  };

  // Sync URL
 useEffect(() => {
  const params = new URLSearchParams();

  params.set('page', page);
  params.set('limit', limit);

  if (rating) params.set('rating', rating);
  if (userType) params.set('userType', userType);
  if (search) params.set('search', search);
  if (sortBy)
  params.set('sortBy', sortBy);

if (sortOrder)
  params.set('sortOrder', sortOrder);

  setSearchParams(params);
}, [
  page,
  limit,
  rating,
  userType,
  search,
  sortBy,
  sortOrder,
  setSearchParams,
]);

  const handleFilterChange = (filterType, value) => {
    setPage(1);

    if (filterType === 'rating') setRating(value);
    if (filterType === 'userType') setUserType(value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (column) => {
  const newOrder =
    sortBy === column &&
    sortOrder === 'asc'
      ? 'desc'
      : 'asc';

  setSortBy(column);
  setSortOrder(newOrder);
  setPage(1);
};

const SortIcon = ({ column }) => {
  if (sortBy !== column) {
    return (
      <span className="text-gray-400">
        ↕
      </span>
    );
  }

  return sortOrder === 'asc'
    ? <span>↑</span>
    : <span>↓</span>;
};

const handleToggleVisibility = (review) => {
  const key = review.bookingId + review.reviewType;

  setTogglingKey(key);

  toggleVisibility(
    {
      bookingId: review.bookingId,
      reviewType: review.reviewType,
      isVisible: !review.isVisible,
    },
    {
      onSettled: () => setTogglingKey(null),
    }
  );
};

  // Star rating renderer
  const renderStars = (count) => {
    return (
      <div className='flex items-center gap-0.5'>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={12}
            className={
              star <= count
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground/30'
            }
          />
        ))}
      </div>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'bg-green-500/10 text-green-600';
    if (rating === 3) return 'bg-yellow-500/10 text-yellow-600';
    return 'bg-red-500/10 text-red-600';
  };

  const getUserTypeColor = (type) => {
    const colors = {
      customer: 'bg-blue-500/10 text-blue-600',
      seller: 'bg-purple-500/10 text-purple-600',
    };
    return colors[type] || 'bg-gray-500/10 text-gray-600';
  };

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>
            Error loading reviews
          </div>
          <p className='text-muted-foreground'>
            {error?.message || 'Something went wrong'}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`p-6 space-y-6 ${PAGE_CONTENT_WIDTH}`}>

        {/* Header */}
        <PageHeader
          icon={MessageSquare}
          title='Reviews'
          subtitle='Manage and moderate platform reviews'
          actions={
            !showPermissionError && (
              <button
                onClick={handleExport}
                className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
              >
                <Download size={16} />
                <span>Export</span>
              </button>
            )
          }
        />

        {/* Filters */}
        <div className='bg-card border border-border rounded-lg p-4'>
<div className='grid grid-cols-1 md:grid-cols-4 gap-4'>

    {/* Search */}
<div>
  <label className='block text-sm font-medium mb-2'>
    Search
  </label>

  <div className='relative'>
    <Search
      size={16}
      className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
    />

    <input
      type='text'
      placeholder='Booking, reviewer, service...'
      value={search}
      onChange={(e) => {
        setSearch(e.target.value);
        setPage(1);
      }}
      className='w-full pl-10 pr-3 py-2 text-sm border border-border rounded-md bg-background'
    />
  </div>
</div>


            {/* Rating Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>Rating</label>
              <select
                value={rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Ratings</option>
                <option value='5'>5 Stars</option>
                {/* <option value='4.5'>4.5 Stars</option> */}
                <option value='4'>4 Stars</option>
                {/* <option value='3.5'>3.5 Stars</option> */}
                <option value='3'>3 Stars</option>
                {/* <option value='2.5'>2.5 Stars</option> */}
                <option value='2'>2 Stars</option>
                {/* <option value='1.5'>1.5 Stars</option> */}
                <option value='1'>1 Star</option>
              </select>
            </div>

            {/* User Type Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>
                User Type
              </label>
              <select
                value={userType}
                onChange={(e) =>
                  handleFilterChange('userType', e.target.value)
                }
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Users</option>
                <option value='customer'>Customer</option>
                <option value='seller'>Seller</option>
              </select>
            </div>

            {/* Items Per Page */}
            <div>
              <label className='block text-sm font-medium mb-2'>
                Items Per Page
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setPage(1);
                }}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value='5'>5</option>
                <option value='10'>10</option>
                <option value='20'>20</option>
              </select>
            </div>

          </div>
        </div>

        {/* Table */}
      <div className='border rounded-lg overflow-hidden'>
  <table className='w-full'>
    <thead className='bg-muted'>
      <tr>
        {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Booking #
        </th> */}

        <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("bookingNumber")}
>
  <div className="flex items-center gap-1">
    Booking #
    <SortIcon column="bookingNumber" />
  </div>
</th>
        {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Reviewer
        </th> */}

        <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("reviewerName")}
>
  <div className="flex items-center gap-1">
    Reviewer
    <SortIcon column="reviewerName" />
  </div>
</th>
        <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Review Type
        </th>
        {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Service
        </th> */}

        <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("bookingItemName")}
>
  <div className="flex items-center gap-1">
    Service
    <SortIcon column="bookingItemName" />
  </div>
</th>
        {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Rating
        </th> */}

        <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("rating")}
>
  <div className="flex items-center gap-1">
    Rating
    <SortIcon column="rating" />
  </div>
</th>
        <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Review
        </th>
        {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Dated
        </th> */}

        <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("ratedAt")}
>
  <div className="flex items-center gap-1">
    Dated
    <SortIcon column="ratedAt" />
  </div>
</th>
        <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Visibility
        </th>
        <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
          Action
        </th>
      </tr>
    </thead>

    <tbody>
      {showPermissionError ? (
        <tr>
          <td
            colSpan='9'
            className='px-4 py-8 text-center text-destructive'
          >
            Error loading reviews: You do not have permission to
            perform this action!
          </td>
        </tr>
      ) : isLoading ? (
        <tr>
          <td colSpan='9' className='text-center p-6'>
            Loading...
          </td>
        </tr>
      ) : reviews.length === 0 ? (
        <tr>
          <td colSpan='9' className='text-center p-6'>
            No reviews found
          </td>
        </tr>
      ) : (
        reviews.map((review, index) => (
          <tr key={review.bookingId + review.reviewType + index} className='border-t'>

            {/* Booking Number */}
            <td className='px-4 py-3'>
              <span className='text-sm font-medium text-primary'>
                {review.bookingNumber || '--'}
              </span>
            </td>

            {/* Reviewer */}
           <td className='px-4 py-3'>
  <div className='flex items-center space-x-3'>
    
    {review?.profilePhoto ? (
      <img
        src={review.profilePhoto}
        alt={review?.reviewerName}
        className='w-10 h-10 rounded-full object-cover flex-shrink-0'
      />
    ) : (
      <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0'>
        <User size={18} />
      </div>
    )}

    <div>
      <p className='text-sm font-medium whitespace-nowrap'>
        {review?.reviewerName || '--'}
      </p>
    </div>

  </div>
</td>

            {/* Review Type */}
            <td className='px-4 py-3'>
              <span
                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getUserTypeColor(
                  review.reviewType
                )}`}
              >
                {review.reviewType || '--'}
              </span>
            </td>

            {/* Service */}
            <td className='px-4 py-3'>
              <span className='text-sm text-muted-foreground'>
                {
  review?.serviceName &&
  review.serviceName !== '-'
    ? review.serviceName
    : review?.packageName &&
      review.packageName !== '-'
      ? review.packageName
      : review?.tourName &&
        review.tourName !== '-'
        ? review.tourName
        : review?.restaurantName || '--'
}
              </span>
            </td>

            {/* Rating */}
            <td className='px-4 py-3'>
              <div className='flex flex-col gap-1'>
                {renderStars(Math.round(review.rating))}
                <span
                  className={`inline-flex w-fit px-2 py-0.5 text-xs font-medium rounded-full ${getRatingColor(
                    review.rating
                  )}`}
                >
                  {review.rating}/5
                </span>
              </div>
            </td>

            {/* Review Text */}
            <td className='px-4 py-3 max-w-[200px]'>
              <p className='text-sm text-muted-foreground line-clamp-2'>
                {review.review || '--'}
              </p>
            </td>

            {/* Date */}
            <td className='px-4 py-3'>
              <span className='text-sm text-muted-foreground whitespace-nowrap'>
                {formatDateTime(review.ratedAt)}
              </span>
            </td>

            {/* Visibility Badge */}
            <td className='px-4 py-3'>
              <span
                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  review.isVisible
                    ? 'bg-green-500/10 text-green-600'
                    : 'bg-gray-500/10 text-gray-500'
                }`}
              >
                {review.isVisible ? 'Visible' : 'Hidden'}
              </span>
            </td>

            {/* Toggle Action */}
            <td className='px-4 py-3'>
              <button
                onClick={() => handleToggleVisibility(review)}
                disabled={togglingKey === review.bookingId + review.reviewType}                className={`inline-flex items-center px-3 py-1 text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  review.isVisible
                    ? 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                    : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                }`}
              >
                {review.isVisible ? (
                  <>
                    <EyeOff size={14} className='mr-1' />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye size={14} className='mr-1' />
                    Show
                  </>
                )}
              </button>
            </td>

          </tr>
        ))
      )}
    </tbody>
  </table>

  {/* Pagination */}
  {!showPermissionError && pagination.totalPages > 1 && (
    <div className='px-4 py-3 border-t border-border flex items-center justify-between'>

      <div className='text-sm text-muted-foreground'>
        Showing{' '}
        {(pagination.page - 1) * limit + 1} to{' '}
        {Math.min(pagination.page * limit, pagination.total)} of{' '}
        {pagination.total} results
      </div>

      <div className='flex items-center space-x-2'>

        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          <ChevronLeft size={16} />
        </button>

        <div className='flex items-center space-x-1'>
          {[...Array(pagination.totalPages)].map((_, idx) => {
            const pageNum = idx + 1;
            const current = pagination.page;

            if (
              pageNum === 1 ||
              pageNum === pagination.totalPages ||
              (pageNum >= current - 1 && pageNum <= current + 1)
            ) {
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                    pageNum === current
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                  }`}
                >
                  {pageNum}
                </button>
              );
            } else if (
              pageNum === current - 2 ||
              pageNum === current + 2
            ) {
              return (
                <span key={pageNum} className='text-muted-foreground'>
                  ...
                </span>
              );
            }

            return null;
          })}
        </div>

        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === pagination.totalPages}
          className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          <ChevronRight size={16} />
        </button>

      </div>
    </div>
  )}
</div>

      </div>
    </Layout>
  );
};

export default ReviewManagement;